<script setup lang="ts">
import type { TrenchObject } from "@/models/objects/TrenchObject";
import { type IObject, ObjectType } from "@/models/objects/types";
import { notifyError, notifySuccess } from "@/utils/notificationUtils";
import { useI18n } from "vue-i18n";
import { useRoute } from "vue-router";
import { useStore } from "vuex";
import type { Drawing2dImage } from "../../models/Drawing2DImage";
import { removeDrawing2dImage } from "../../utils/cesium-common";

/** props定義 */
const props = defineProps<{
	entity: Drawing2dImage;
	draggable: boolean;
	isEditing: boolean;
}>();

/** 関数定義 */
const store = useStore();
const { t } = useI18n();
const route = useRoute();

/**
 * Delete the 2D Image.
 */
const remove = async () => {
	// Google Analyticsに連携
	addClickTypeLog(GoogleAnalyticsClickTypes.P_CLICK_2D_DELETE);

	// If the image to be deleted is currently being edited or in 2-point alignment, end it first
	const entity = props.entity;
	const currentAssetId = entity.getAssetIdNum();

	if (currentAssetId != null) {
		const imageUnderEdit = store.state.drawing2dImage.imageUnderEdit;
		const imageUnder2PointAlignment =
			store.state.drawing2dImage.imageUnder2PointAlignment;

		const isEditingCurrent =
			imageUnderEdit?.getAssetIdNum?.() === currentAssetId;
		const isAligningCurrent =
			imageUnder2PointAlignment?.getAssetIdNum?.() === currentAssetId;

		if (isEditingCurrent) {
			try {
				// inProgressModeManager/endEdit internally calls cancel2pointsAlignment,
				// so both edit and alignment states will be cleaned up.
				await store.dispatch("inProgressModeManager/endEdit");
			} catch (e) {
				console.error(
					"failed to end edit mode before deletion (assetId:",
					currentAssetId,
					"):",
					e,
				);
				return;
			}
		} else if (isAligningCurrent) {
			try {
				// Cancel 2-point alignment before deletion.
				// Dispatch cancel2pointsAlignment to update Vuex state,
				// allowing dependent watchers to complete cleanup.
				await store.dispatch("drawing2dImage/cancel2pointsAlignment");
			} catch (e) {
				console.error(
					"failed to cancel 2-point alignment before deletion (assetId:",
					currentAssetId,
					"):",
					e,
				);
				return;
			}
		}
	}

	// Update object list
	try {
		await store.dispatch("object/get_obj_list", route.query.siteId);
	} catch {
		notifyError(
			{ $store: store, $t: t },
			`${t("REFRESH_OBJECT_LIST")} ${t("failed")}`,
		);
		return;
	}

	// Return the object that is associated assetId of the 2D image to be deleted
	const associateObject = store.state.object.objects.find((obj: IObject) => {
		const associatedId =
			obj.type === ObjectType.TRENCH
				? (obj as TrenchObject).rubbingDestination.id
				: obj.associatedTerrain.id;

		return currentAssetId === associatedId;
	});

	// Show the error message on the snackbar if the 2D image is associated any object
	if (associateObject) {
		notifyError(
			{ $store: store, $t: t },
			t("CANNOT_DELETE_ASSOCIATED_POINT_CLOUD"),
		);
		return;
	}

	try {
		await store.dispatch("drawing2dImage/deleteDrawing2dImage", {
			siteId: route.query.siteId,
			drawing2dImage: props.entity,
		});

		removeDrawing2dImage(props.entity);

		// remove sucess
		notifySuccess(
			{ $store: store, $t: t },
			`${props.entity.getName()} ${t("REMOVE")} ${t("successful")}`,
		);
	} catch (e) {
		// remove failed
		notifyError(
			{ $store: store, $t: t },
			`${props.entity.getName()} ${t("REMOVE")} ${t("failed")}`,
		);
		console.error(e);
		return;
	}
};
</script>

<template>
  <span>
    <span v-if="timeStamp && !isEditing" class="ml-4">{{ timeStamp }}</span>
    <v-card-actions v-if="!isEditing" class="py-0 list-item" :style="isHighlighted ? 'background-color: #0064BA;' : ''">
      <v-btn v-if="draggable" icon size="24" class="handle" style="cursor: grab">
        <v-icon> mdi-drag </v-icon>
      </v-btn>
      <v-btn icon size="24" @click="toggle_visibility" :disabled="isDisabledVisibilityBtn" class="ml-0">
        <v-icon>
          {{ entity.getIsVisible() ? "icon:View" : "icon:View-Off" }}
        </v-icon>
      </v-btn>
      <v-btn icon size="28" @click="toggle_expansion" :disabled="isEditOnProgress" class="ml-0">
        <v-icon>
          mdi-play {{ entity.getExpansion() && !isEditOnProgress ? "mdi-rotate-90" : "" }}
        </v-icon>
      </v-btn>
      <ListItemLabel :text="entity?.getName() ?? ''" icon="mdi-drawing-box" />
      <v-btn @click="focus" :disabled="isDisabledFocusBtn" icon size="24">
        <v-icon size="small">icon:Search</v-icon>
      </v-btn>
      <v-menu transition="slide-y-transition" :disabled="entity.getTilingProgress()">
        <template v-slot:activator="{ props }">
          <v-btn icon v-bind="props" size="24">
            <v-icon> icon:Overflow-Menu-Vertical </v-icon>
          </v-btn>
        </template>
        <v-list density="compact">
          <v-list-item @click="edit" :disabled="isEditOnProgress || isDisabledAssetActions">
            <v-icon class="mr-4">icon:Pencil</v-icon>
            <span style="font-size: 13px">{{ $t("EDIT") }}</span>
          </v-list-item>
          <v-list-item @click="start2PointAlignment" :disabled="isDisabledAssetActions">
            <v-icon class="mr-4">mdi-map-marker-multiple</v-icon>
            <span style="font-size: 13px">{{ $t("TWO_POINT_ALIGNMENT") }}</span>
          </v-list-item>
          <v-list-item :disabled="!entity.isLoaded || isDisabledAssetActions" @click="exportLandXML">
            <v-icon class="mr-4">icon:Export</v-icon>
            <span style="font-size: 13px">{{ $t("EXPORT_AS") }}</span>
          </v-list-item>
          <v-list-item @click="remove">
            <v-icon class="mr-4">mdi-delete</v-icon>
            <span style="font-size: 13px">{{ $t("REMOVE") }}</span>
          </v-list-item>
        </v-list>
      </v-menu>
      <!-- 処理ステータス -->
      <div v-if="entity.getProcessingStatus()">
        <processing-status :status="entity.getProcessingStatus()"
          :processingStatus="drawing2dProcessingStatus">
        </processing-status>
      </div>
    </v-card-actions>
    <div class="pl-7 mb-2" v-show="isEditing || (entity.getExpansion() && !isEditOnProgress)">
      <v-card-actions class="py-0 px-5">
        {{ $t("OPACITY") }}
        <v-slider class="ml-1" v-model="transparency" :disabled="isDisabledTransparency" @end="saveSettings"
          hide-details max="1" step="0.01" color="primary" track-size="0.5" thumb-size="14"></v-slider>
      </v-card-actions>
      <v-card-actions class="py-0 px-5 d-flex">
        <span class="mr-4 align-self-end">{{ $t("ELEVATION") }}</span>
        <v-form ref="inputElevation" v-model="elevationFormValid" @submit.prevent>
          <DistanceSettingInput variant="underlined" hide-details density="compact" :min-value="-999999.999"
            :step="0.001" :max-value="999999.999" :decimal-length="3" :disabled="!isEnabledElevation"
            @blur="onBlurElevation" v-model.number="elevation" class="flex-grow-1 align-self-center" :draggable="true"
            :ondragstart="e => e.preventDefault()" :onkeydown="e => e.code !== 'KeyE'" />
        </v-form>
      </v-card-actions>
    </div>
  </span>
</template>

<script lang="ts">
import { type PropType, defineComponent } from "vue";
import DistanceSettingInput from "@/components/Project/DistanceSettingInput.vue";
import ProcessingStatus from "@/components/common/ProcessingStatus.vue";
import ListItemLabel from "@/components/Project/ListItemLabel.vue";
import * as constants from "@/constant.js";
import { drawing2dProcessingStatus } from "@/models/drawing2dProcessingStatus";
import * as cesiumCommon from "@/utils/cesium-common";
import dayjs from "dayjs";
import isSameOrAfter from "dayjs/plugin/isSameOrAfter";
import ProgressCircle from "./ProgressCircle.vue";
import { mapActions } from "vuex/dist/vuex.cjs.js";
import { mapState, mapMutations } from "vuex";

import {
  addClickTypeLog,
  GoogleAnalyticsClickTypes,
} from "../../utils/googleAnalytics.js";
import { InProgressMode } from "@/store/modules/inprogress-mode-manager.js";

dayjs.extend(isSameOrAfter);

export default defineComponent({
  name: "Drawing2dImageListItem",
  props: {
    entity: { type: Object as PropType<Drawing2dImage>, required: true },
    draggable: { type: Boolean, required: true },
    isEditing: { type: Boolean, required: true },
  },
  components: {
    ProgressCircle,
    ProcessingStatus,
    ListItemLabel,
    DistanceSettingInput
  },
  data() {
    return {
      showUploadProgress: true,
      isUploadError: false,
      constants,
      drawing2dProcessingStatus,
      elevation: NaN,
      elevationFormValid: null,
    };
  },
  computed: {
    ...mapState("drawing2dImage", [
      "pointsOfTwoPointAlignment",
      "isTwoPointAlignment",
      "imageUnder2PointAlignment",
      "imageUnderEdit"
    ]),
    isAssetUploaded() {
      return this.entity.getIsAssetUploaded();
    },
    /**
     * 画面のクリエーションモードの状態
     */
    inCreationMode() {
      return this.$store.state.inProgressModeManager.inProgressMode === InProgressMode.CREATION && this.$store.state.inProgressModeManager.objectType === "Drawing2DImage";
    },
    /**
     * 点群の透明度
     */
    // explicit `this`: a mapState spread defeats ThisType on get/set computeds
    transparency: {
      get(this: { entity: Drawing2dImage }): number {
        return this.entity.getTransparency();
      },
      set(this: { entity: Drawing2dImage }, value: number) {
        this.entity.setTransparency(value);
      },
    },
    /**
     * 可視状態切り替えボタンの活性状態を制御する
     * 以下の場合非活性
     * ・道路追加や平場追加を実行中で他の点群が活性状態じゃない
     * ・タイリング途中
     * ・アセットがアップロードされていない
     */
    isDisabledVisibilityBtn() {
      return (
        (this.inCreationMode && !this.entity.getIsCreating()) ||
        this.entity.getTilingProgress() ||
        this.isAssetUploaded === "PENDING" ||
        this.isAssetUploaded === "ERROR"
      );
    },
    /**
     * 図面そのものを操作するメニュー（編集 / 2 点合わせ / エクスポート）の非活性状態
     * DTM / オルソ経路では処理中・エラーの行も一覧に出るため、可視ボタンと同じ条件で弾く
     */
    isDisabledAssetActions() {
      return (
        this.isAssetUploaded === "PENDING" || this.isAssetUploaded === "ERROR"
      );
    },
    /**
     * 透明度スライダーの活性状態を制御する
     */
    isDisabledTransparency() {
      // 非表示状態の場合は非活性
      return !this.entity.getIsVisible();
    },
    /**
     * Whether the image is draped on the terrain surface (elevation is NaN or null)
     */
    isDraped() {
      const elevation = this.entity.getElevation();
      return elevation == null || Number.isNaN(Number(elevation));
    },
    /**
     * 標高の活性状態を制御する
     */
    isEnabledElevation() {
      // 地表面に投影の場合は非活性
      return !this.isDraped && this.isEditOnProgress;
    },
    /**
     * フォーカスボタンの活性状態を制御する
     */
    isDisabledFocusBtn() {
      // 非表示状態の場合は非活性
      return !this.entity.getIsVisible();
    },
    timeStamp() {
      return this.entity.getTimeStamp();
    },
    isEditOnProgress() {
      return this.imageUnderEdit?.getId() === this.entity.getId();
    },
    editTargetId() {
      return this.imageUnderEdit?.getId();
    },
    is2PointAlignmentOnProgress() {
      return this.isTwoPointAlignment && this.imageUnder2PointAlignment?.getId() === this.entity.getId();
    },
    isHighlighted() {
      return this.is2PointAlignmentOnProgress || this.isEditOnProgress;
    },
    entityElevation() {
      return this.entity.getElevation();
    }
  },
  watch: {
    entityElevation: {
      handler(elevationValue) {
        if (this.entity.getIsVisible()) {
          const isDraped = elevationValue == null || Number.isNaN(Number(elevationValue));
          this.elevation = isDraped ? undefined : Number(elevationValue);
        }
      },
      immediate: true
    },
    // アップロードステータスがPENDINGになれば再度ポーリング開始
    isAssetUploaded(newValue) {
      if (newValue === "PENDING") {
        this.showUploadProgress = true;
        this.isUploadError = false;
      }
    },
    transparency() {
      let transparency_val = parseFloat(this.transparency.toFixed(2));
      try {
        cesiumCommon.updateDrawing2dImageTransparency(
          this.entity,
          transparency_val
        );
      } catch (e) {
        console.error(e);
      }
    },
    inCreationMode(newCreationMode) {
      if (newCreationMode) {
        // 画面がクリエーションモードに変更になった場合
        if (this.entity.getIsVisible()) {
          // 可視状態であれば点群をクリエーションモードに変更
          this.entity.setIsCreating(true);
        }
      } else {
        // 画面のクリエーションモードが解除された場合
        if (this.entity.getIsCreating()) {
          // 点群がクリエーションモードであれば解除
          this.entity.setIsCreating(false);
        }
      }
    },
    elevationFormValid(newVal) {
      if (this.isEditing) this.$emit("update:valid", newVal !== false);
    },
  },
  methods: {
    ...mapMutations(["set_snackbar", "reset_snackbars"]),
    ...mapActions("drawing2dImage", ["setIsTwoPointAlignment", "addPointForTwoPointsAlignment", "deleteAllPointsForTwoPointsAlignment"]),

    async toggle_visibility() {
      await cesiumCommon.toggleDrawing2dImageVisibility(this.entity);
      // visibility is changed to be visible
      if (this.entity.getIsVisible()) {
        const elevationValue = this.entity.getElevation();
        const isDraped = elevationValue == null || Number.isNaN(Number(elevationValue));
        this.elevation = isDraped ? undefined : Number(elevationValue);
      }
    },
    toggle_expansion() {
      this.entity.setExpansion(!this.entity.getExpansion());
    },
    focus() {
      cesiumCommon.zoomToDrawing2dImage(this.entity);
    },
    saveSettings() {
      cesiumCommon.uploadDrawing2dImage(this.entity.getId(), this.$route.query.siteId);
    },
    async edit() {
      if (!this.isEditOnProgress) {
        // Google Analyticsに連携
        addClickTypeLog(GoogleAnalyticsClickTypes.P_CLICK_2D_EDIT);


        if (!this.entity.getIsVisible()) {
          // if image is invisible, display it
          await this.toggle_visibility();
        }
        const elevationValue = this.entity.getElevation();
        const isDraped = elevationValue == null || Number.isNaN(Number(elevationValue));
        this.elevation = isDraped ? undefined : Number(elevationValue);
        // await this.$store.dispatch("inProgressModeManager/startEdit");
        await this.$store.dispatch("drawing2dImage/startEdit", {
          drawing2dImage: this.entity,
          onError: (e) => {
            console.warn(e);
            notifyError(this, `${this.$t("EDIT")} ${this.$t("failed")}`);
          },
          onCancel: () => {
            this.$store.dispatch("inProgressModeManager/cancelEdit");
          },
          onFinish: async () => {
            cesiumCommon.uploadDrawing2dImage(this.entity.getId(), this.$route.query.siteId);
          }
        });
      }
      else {

        if (!await this.validateElevation()) {
          // Write back to previous saved value if invalid elevation value
          this.elevation = this.entity.getElevation();
        }
        await this.$store.dispatch("inProgressModeManager/endEdit");
      }
    },
    async start2PointAlignment() {
      if (this.is2PointAlignmentOnProgress) {
        return;
      }
      const started = await this.$store.dispatch("inProgressModeManager/startEdit");
      if (!started) return;

      // Google Analyticsに連携
      addClickTypeLog(GoogleAnalyticsClickTypes.P_CLICK_2D_2POINT_ALIGNMENT);

      if (!this.entity.getIsVisible()) {
        // if image is invisible, display it
        await this.toggle_visibility();
      }
      const reset2PointAlignment = async () => {
        this.reset_snackbars();
        this.deleteAllPointsForTwoPointsAlignment();
        this.setIsTwoPointAlignment({ flag: false });
        this.$store.getters.viewer.updateDrawing2dImageVisibility(
          this.entity.getId(),
          this.entity.getIsVisible(),
        );
        await this.$store.dispatch("inProgressModeManager/endEdit");
      }
      this.$store.dispatch("drawing2dImage/start2pointsAlignment", {
        drawing2dImage: this.entity,
        onStart: () => {
          this.setIsTwoPointAlignment({ flag: true, imageUnder2PointAlignment: this.entity });
        },
        onError: async (e) => {
          await reset2PointAlignment();
          notifyError(this, `${this.$t("EDIT")} ${this.$t("failed")}`);
        },
        onCancel: async () => {
          await reset2PointAlignment();
        },
        onFinish: async () => {
          await reset2PointAlignment();
          cesiumCommon.uploadDrawing2dImage(this.entity.getId(), this.$route.query.siteId);
        }
      });
    },
    async exportLandXML() {
      const geometry = this.$store.getters.viewer.getDrawing2DImageMeshGeometry(this.entity.getId());
      const assetId = this.entity.getId()
      const event =
        this.entity.getHasImage() ?
          new CustomEvent("openPropertyDialog", {
            detail: { name: this.entity.getName(), geometry, type: 12, imageId: assetId },
          })
          : new CustomEvent("openPropertyDialog", {
            detail: { type: 6, name: this.entity.getName(), geometry },
          });
      window.dispatchEvent(event);
    },
    async onBlurElevation() {
      const isValidElevation = await this.validateElevation();
      if (isValidElevation && this.elevation !== this.entity.getElevation()) {
        // Update elevation only if validation is success and elevation is changed value
        await this.$store.getters.viewer.setElevationOfDrawing2dImage(this.entity.getId(), this.elevation);
      }
    },
    async validateElevation() {
      const validate = await this.$refs.inputElevation.validate();
      // Return true if no validation error, otherwise false
      return validate.valid;
    },
  },
  async beforeUnmount() {
    // Clean up edit and alignment states when component is unmounted.
    // Note: Vue does not await async beforeUnmount; ensure state cleanup is robust.
    try {
      if (this.isEditOnProgress) {
        try {
          await this.$store.dispatch("drawing2dImage/endEdit");
        } catch (e) {
          console.error("failed to end edit during unmount:", e);
        }
        try {
          await this.$store.dispatch("inProgressModeManager/endEdit");
        } catch (e) {
          console.error("failed to end progress mode during unmount:", e);
        }
      }
    } finally {
      // Ensure alignment is always canceled if still active,
      // even if endEdit failed or did not cancel it.
      if (this.$store.state.drawing2dImage?.isTwoPointAlignment) {
        try {
          await this.$store.dispatch("drawing2dImage/cancel2pointsAlignment");
        } catch (e) {
          console.error("failed to cancel alignment during unmount:", e);
        }
      }
    }
  },
});
</script>

<style>
.list-item {
  height: 28px;
  align-items: center;
  gap: 4px;
}
</style>